import { useEffect, useState } from 'react';
import { crm, type WavePreviewDto, type WaveStartDto } from '@/lib/crm-client';
import { startAbgewiesen, versanddauer } from '@/lib/campaign-schedule';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface Props {
  campaignId: string;
  onClose: () => void;
  /** Nach dem Start — der Aufrufer lädt seine Zahlen neu. */
  onSent: () => void;
}

/**
 * Zeigt eine Welle, bevor sie losgeht — und schickt sie erst auf den zweiten
 * Klick.
 *
 * Der Server verschickt nichts von selbst; dieser Dialog ist die einzige
 * Stelle, an der eine echte Erstansprache ausgelöst wird. Deshalb steht hier
 * alles, was vor dem Klick zählt: wie viele, aus welchem Postfach, wie viel
 * vom Tageslimit noch übrig ist, wie ausgelastet das Postfach heute schon
 * ist, wie lange es mit den Pausen dauert, und ein paar aufgelöste Beispiele
 * statt bloßer Namen.
 *
 * Der Klick startet die Welle nur: mit den Pausen läuft sie eine Viertelstunde
 * und länger im Hintergrund. Das Ergebnis steht danach im Protokoll, der
 * Fortschritt in der Mitgliederliste.
 */
export function WavePreviewDialog({ campaignId, onClose, onSent }: Props) {
  const [vorschau, setVorschau] = useState<WavePreviewDto | null>(null);
  const [konto, setKonto] = useState<{ label: string; fromAddress: string } | null>(null);
  const [pause, setPause] = useState<{ min: number; max: number } | null>(null);
  // Nur zum Warnen, falls die Kampagne nicht aktiv ist — `sendWave` prueft das
  // selbst noch einmal und weist den Start sonst ab.
  const [nichtAktiv, setNichtAktiv] = useState(false);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [gestartet, setGestartet] = useState<WaveStartDto | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    void Promise.all([crm.campaigns.wavePreview(campaignId), crm.campaigns.get(campaignId)])
      .then(async ([vp, kampagne]) => {
        if (abgebrochen) return;
        setVorschau(vp);
        setNichtAktiv(kampagne?.status !== 'aktiv');
        if (kampagne) setPause({ min: kampagne.sendPauseMinSec, max: kampagne.sendPauseMaxSec });
        if (kampagne?.senderAccountId) {
          const konten = await crm.mailAccounts.list();
          const gefunden = konten.find((k) => k.id === kampagne.senderAccountId);
          if (!abgebrochen && gefunden) {
            setKonto({ label: gefunden.label, fromAddress: gefunden.fromAddress });
          }
        }
      })
      .catch((err: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(err instanceof Error ? err.message : 'Vorschau konnte nicht geladen werden');
        }
      });
    return () => {
      abgebrochen = true;
    };
  }, [campaignId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const senden = async () => {
    setBusy(true);
    setSendeFehler(null);
    try {
      const res = await crm.campaigns.sendWave({ campaignId });
      if (res.started) {
        setGestartet(res);
        onSent();
      } else {
        setSendeFehler(res.reason ? startAbgewiesen(res.reason) : 'Die Welle wurde nicht gestartet.');
      }
    } catch (err) {
      setSendeFehler(err instanceof Error ? err.message : 'Versand fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Nächste Welle"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="my-auto w-full max-w-2xl rounded-lg border bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold">Nächste Welle</h2>

        {ladeFehler && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {ladeFehler}
          </p>
        )}

        {!vorschau && !ladeFehler ? (
          <p className="mt-6 text-sm text-muted-foreground">Lade Vorschau …</p>
        ) : vorschau ? (
          gestartet ? (
            <Gestartet start={gestartet} onClose={onClose} />
          ) : (
            <div className="mt-4 space-y-4">
              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <Kennzahl label="Versandkonto">
                  {konto ? `${konto.label} — ${konto.fromAddress}` : 'Standardkonto'}
                </Kennzahl>
                <Kennzahl label="Tageslimit">
                  {vorschau.limitLeft} von heute noch offen
                </Kennzahl>
                <Kennzahl label="Postfach heute">
                  Dieses Postfach hat heute bereits {vorschau.accountSentToday}{' '}
                  {vorschau.accountSentToday === 1 ? 'Mail' : 'Mails'} verschickt
                </Kennzahl>
                <Kennzahl label="Kürzlich geschrieben">
                  {vorschau.recentlyWroteUs} {vorschau.recentlyWroteUs === 1 ? 'Empfänger hat' : 'Empfänger haben'}{' '}
                  dir in den letzten sieben Tagen geschrieben
                </Kennzahl>
                {pause && vorschau.candidates.length > 0 && (
                  <Kennzahl label="Dauer">
                    {versanddauer(vorschau.candidates.length, pause)} — {pause.min}–{pause.max} Sekunden
                    Pause zwischen den Mails
                  </Kennzahl>
                )}
              </dl>

              {nichtAktiv && (
                <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
                  Diese Kampagne ist nicht aktiv — erst auf „Aktiv“ stellen, dann geht wirklich
                  etwas raus.
                </p>
              )}

              {vorschau.candidates.length === 0 ? (
                <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
                  {/*
                    `blocked` kommt aus derselben Pruefung, an der auch der
                    Versand haengenbliebe — vorher stand hier ein „entweder …
                    oder“, weil die Vorschau den Grund nicht kannte.
                  */}
                  {vorschau.blocked === 'keine_vorlage'
                    ? 'Der Erstansprache fehlt die Vorlage. Ohne sie geht nichts raus — im Reiter „Schritte“ verfassen.'
                    : vorschau.blocked === 'kein_schritt'
                      ? 'Diese Kampagne hat noch keinen Schritt. Im Reiter „Schritte“ die Erstansprache anlegen.'
                      : vorschau.totalOpen === 0
                        ? 'Keine offenen Mitglieder — diese Welle hätte nichts zu verschicken.'
                        : 'Nichts versandbereit — das Tageslimit ist für heute ausgeschöpft.'}
                </p>
              ) : (
                <>
                  <div>
                    <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Stichproben
                    </h3>
                    <div className="mt-2 space-y-2">
                      {vorschau.candidates.slice(0, 5).map((c) => (
                        <Card key={c.memberId} className="p-3 text-sm">
                          <p className="font-medium">
                            {c.name}
                            {c.organizationName && (
                              <span className="font-normal text-muted-foreground">
                                {' '}
                                · {c.organizationName}
                              </span>
                            )}
                            <span className="font-normal text-muted-foreground"> · {c.toEmail}</span>
                          </p>
                          <p className="mt-1 font-medium">{c.subject || '(ohne Betreff)'}</p>
                          <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                            {c.bodyPreview}
                            {c.bodyPreview.length >= 200 && ' …'}
                          </p>
                        </Card>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Alle Empfänger ({vorschau.candidates.length})
                    </h3>
                    <div className="mt-2 max-h-48 overflow-y-auto rounded-md border">
                      <ul className="divide-y text-sm">
                        {vorschau.candidates.map((c) => (
                          <li key={c.memberId} className="flex justify-between gap-3 px-3 py-1.5">
                            <span className="truncate">
                              {c.name}
                              {c.organizationName && ` · ${c.organizationName}`}
                            </span>
                            <span className="shrink-0 text-muted-foreground">{c.toEmail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              )}

              {sendeFehler && (
                <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {sendeFehler}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose} disabled={busy}>
                  Abbrechen
                </Button>
                <Button
                  onClick={() => void senden()}
                  disabled={busy || nichtAktiv || vorschau.candidates.length === 0}
                >
                  {busy
                    ? 'Starte …'
                    : `${vorschau.candidates.length} Erstansprachen senden`}
                </Button>
              </div>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}

function Kennzahl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

/** Die Welle läuft — was jetzt passiert und wo man es sieht. */
function Gestartet({ start, onClose }: { start: WaveStartDto; onClose: () => void }) {
  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm">
        <strong>Die Welle läuft.</strong> {start.planned}{' '}
        {start.planned === 1 ? 'Erstansprache geht' : 'Erstansprachen gehen'} nacheinander raus,{' '}
        {versanddauer(start.planned, start.pauseSec)}.
      </p>
      <p className="text-sm text-muted-foreground">
        Du kannst den Dialog schließen — der Versand läuft auf dem Server weiter. Den Fortschritt
        siehst du in der Mitgliederliste, das Ergebnis danach im Protokoll. „Pausieren“ hält die
        Welle vor der nächsten Mail an. Lehnt der Anbieter vorübergehend ab, hält sie von selbst an.
      </p>
      <div className="flex justify-end">
        <Button onClick={onClose}>Fertig</Button>
      </div>
    </div>
  );
}
